/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    useTheme
} from '@mui/material';
import { Iconify } from 'src/components';
import { resolveWhatsappPreferred } from 'src/utils/mobileContactHandler';
import {
    arcBtnStyle,
    archivingButtonStyle,
    badgeStyle,
    flwBtnStyle,
    menuListStyle,
    recordAttendedButtonStyle,
    viewBtnStyle,
    arrowButtonStyle,
    buttonGroupStyle,
} from 'src/styles/NewLeadTableRowStyle';

const ContactCell = ({
    row,
    anchorRef,
    open,
    setOpen,
    isMobile,
    buttonHandleClose,
    handlePopup,
    viewDetailBtn,
    handleCompleteTreatment,
    handleAttendedClick,
    handleAttendedFailedClick,
    handleAttendedPopup,
    directRecord = false,
    newLeadTasksMode = false,
}) => {
    const theme = useTheme();
    const handleTogglePopper = (id) => {
        anchorRef.current = document.getElementById(`button-${row.lead_id}`);
        setOpen((prevStates) => ({
            ...prevStates,
            [id]: !prevStates[id],
        }));
    };

    const isPopperOpen = (id) => Boolean(open[id]);

    if (row.is_archive_pending) {
        return (
            <Badge
                badgeContent={[row?.lead_treatments__lead_contacted_counts__count]}
                color="primary"
                sx={arcBtnStyle(row?.lead_treatments__lead_contacted_counts__count)}
            >
                <Button
                    variant="contained"
                    color="primary"
                    disabled={row.is_archive_pending}
                    sx={{ backgroundColor: 'rgba(145, 158, 171, 0.24)', color: 'rgba(145, 158, 171, 0.8)', minWidth: '150px', p: '6px 14px', fontSize: '0.8125rem' }}
                >
                    Archiving
                </Button>
            </Badge>
        );
    }

    if (row.status === 'CONSULTATION_BOOKED' && !row.is_archive_pending) {
        if (directRecord) {
            const count = row?.lead_treatments__lead_contacted_counts__count;
            return (
                <Badge
                    badgeContent={count}
                    color="primary"
                    overlap="rectangular"
                    invisible={!count || count === 0}
                    sx={{
                        '& .MuiBadge-badge': {
                            bgcolor: 'primary.darker',
                            color: 'common.white',
                            fontSize: '0.65rem',
                            minWidth: 16,
                            height: 16,
                            padding: '0 4px',
                            top: 4,
                            right: 4,
                        },
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAttendedClick(row.lead_id, row)}
                        disabled={row?.appointments?.[0]?.status === 'CANCELLED'}
                        sx={{
                            borderRadius: '8px',
                            p: '6px 14px',
                            fontSize: '0.8125rem',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            boxShadow: '0px 4px 10px rgba(0, 167, 111, 0.24)',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                                boxShadow: '0px 4px 10px rgba(0, 167, 111, 0.24)',
                            },
                        }}
                    >
                        Record Attended
                    </Button>
                </Badge>
            );
        }
        return (
            <Box>
                <Box>
                    <ButtonGroup
                        variant="contained"
                        ref={anchorRef}
                        aria-label="split button"
                        sx={buttonGroupStyle(theme)}
                        disabled={row?.appointments?.[0]?.status === 'CANCELLED'}
                    >
                        <Button onClick={() => handleTogglePopper(row.lead_id)} id={`button-${row.lead_id}`} sx={{ ...recordAttendedButtonStyle, p: '6px 14px' }}>
                            Record Attendance
                        </Button>
                        <Button
                            aria-controls={open ? 'split-button-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            onClick={() => handleTogglePopper(row.lead_id)}
                            id={`button-${row.lead_id}`}
                            sx={arrowButtonStyle(theme)}
                        >
                            <Badge
                                badgeContent={[row?.lead_treatments__lead_contacted_counts__count]}
                                color="primary"
                                sx={badgeStyle(row.status, row?.lead_treatments__lead_contacted_counts__count)}
                            >
                                <Iconify style={{ color: '#fff' }} width={10} height={10} icon={'bxs:right-arrow'} />
                            </Badge>
                        </Button>
                    </ButtonGroup>
                </Box>
                <Popper
                    sx={{
                        zIndex: 1,
                        marginLeft: '10px !important',
                    }}
                    open={isPopperOpen(row.lead_id)}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    transition
                    placement={isMobile ? 'left-start' : 'right-start'}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper>
                                <ClickAwayListener onClickAway={buttonHandleClose}>
                                    <MenuList id="split-button-menu" autoFocusItem sx={menuListStyle}>
                                        <MenuItem
                                            key="Attended"
                                            onClick={() => handleAttendedClick(row.lead_id, row)}
                                            sx={{ borderRadius: '8px', minHeight: '40px !important', color: '#212B36' }}
                                        >
                                            <Iconify
                                                style={{ color: '#212B36', marginRight: 15 }}
                                                width={16}
                                                height={16}
                                                icon={'akar-icons:check'}
                                            />{' '}
                                            Attended
                                        </MenuItem>
                                        <MenuItem
                                            key="Failed to attend"
                                            onClick={() => handleAttendedFailedClick(row.lead_id, row)}
                                            sx={{ borderRadius: '8px', minHeight: '40px !important', color: '#212B36' }}
                                        >
                                            <Iconify
                                                style={{ color: '#212B36', marginRight: 15 }}
                                                width={16}
                                                height={16}
                                                icon={'ci:close-big'}
                                            />{' '}
                                            Failed to attend
                                        </MenuItem>
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>
        );
    }
    if (row.status === 'CONSULTATION_ATTENDED' && !row.is_archive_pending) {
        return (
            <Button
                variant="contained"
                color="primary"
                startIcon={<Iconify icon={'fluent:call-32-filled'} width={16} height={16} />}
                onClick={() => {
                    handleAttendedPopup(row.lead_id, row);
                }}
                disabled={row.is_archive_pending}
                sx={archivingButtonStyle}
            >
                {
                    <Badge
                        badgeContent={[row?.lead_treatments__lead_contacted_counts__count]}
                        color="primary"
                        sx={badgeStyle(row.status, row?.lead_treatments__lead_contacted_counts__count)}
                    >
                        {row.is_archive_pending ? 'Archiving' : 'Follow up'}
                    </Badge>
                }
            </Button>
        );
    }

    // TREATMENT_STARTED: a started treatment is "in progress" — the prominent
    // contact-column button opens the wrap-up HUB (not lead detail). Label =
    // "Treatment in progress", no contacted-count Badge (the stray "0" was noise).
    if (row.status === 'TREATMENT_STARTED' && !row.is_archive_pending) {
        return (
            <Button
                variant="contained"
                color="primary"
                onClick={() => (handleCompleteTreatment
                    ? handleCompleteTreatment(row)
                    : viewDetailBtn(row.lead_id, row.status, row))}
                sx={viewBtnStyle(row?.status)}
            >
                Treatment in progress
            </Button>
        );
    }

    if (row.status === 'CLOSED' && !row.is_archive_pending) {
        return (
            <Button
                variant="contained"
                color="primary"
                onClick={() => viewDetailBtn(row.lead_id, row.status, row)}
                sx={viewBtnStyle(row?.status)}
            >
                <Badge
                    badgeContent={[row?.lead_treatments__lead_contacted_counts__count]}
                    color="primary"
                    sx={badgeStyle(row.status, row?.lead_treatments__lead_contacted_counts__count)}
                >
                    View details
                </Badge>
            </Button>
        );
    }

    if (row.treatment_name === '' || null) {
        return '';
    }

    const isWhatsAppPreferred = row?.originalData?.lead_contact?.preferred_contact_method?.toLowerCase() === "whatsapp";
    const getLabel = rowData => {
        if (newLeadTasksMode) return "Contact lead";
        if (rowData?.status === 'FAILED_TO_ATTEND_CONSULTATION') return "Follow up";
        if (rowData?.lead_treatments__lead_contacted_counts__count === 0) return "Contact lead";
        return "Follow up"
    }
    const buttonContent = newLeadTasksMode ? getLabel(row) : (
        <Badge
            badgeContent={[row?.lead_treatments__lead_contacted_counts__count]}
            color="primary"
            sx={badgeStyle(row.status, row?.lead_treatments__lead_contacted_counts__count)}
        >
            {getLabel(row)}
        </Badge>
    );

    return (
        <Button
            variant="outlined"
            color="primary"
            startIcon={!row.is_archive_pending && <Iconify icon={isWhatsAppPreferred ? 'ri:whatsapp-fill' : 'fluent:call-32-filled'} width={16} height={16} />}
            onClick={() => handlePopup(row.lead_id, row.status, row, resolveWhatsappPreferred(row))}
            disabled={row.is_archive_pending}
            sx={flwBtnStyle(row.is_archive_pending)}
        >
            {buttonContent}
        </Button>
    );
};

export default React.memo(ContactCell);

ContactCell.propTypes = {
    row: PropTypes.any,
    anchorRef: PropTypes.any,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    isMobile: PropTypes.bool,
    buttonHandleClose: PropTypes.func,
    handlePopup: PropTypes.func,
    viewDetailBtn: PropTypes.func,
    handleCompleteTreatment: PropTypes.func,
    handleAttendedClick: PropTypes.func,
    handleAttendedFailedClick: PropTypes.func,
    handleAttendedPopup: PropTypes.func,
    handleSyncDentally: PropTypes.func,
    syncingLeadIds: PropTypes.object,
    directRecord: PropTypes.bool,
    newLeadTasksMode: PropTypes.bool,
};
